import {
  Badge,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react';
import { ImageWithSkeleton } from 'features/common/components/ImageWithSkeleton';
import { history } from 'features/common/history/history';
import {
  getDeleteScopedContextRoute,
  getEditScopedContextRoute,
} from 'features/context-ids/routes';
import { FilteredContextWithResources } from 'features/context-ids/types/types';
import {
  getResourceDisplayConfig,
  isDisplayableResource,
  isSensitiveResource,
} from 'features/context-ids/utils';
import { SensitiveResourceIcon } from 'features/context-ids/components/SensitiveResourceIcon';
import {
  CONTEXT_IDS_TEXT,
  TenantProductCode,
} from 'features/context-ids/constants';

export const ResourcesCellPopover = ({
  resources,
}: Partial<FilteredContextWithResources>) => (
  <Popover trigger='hover'>
    <PopoverTrigger>
      <Text
        as='button'
        type='button'
        border='none'
        background='none'
        padding={0}
        cursor='pointer'
        textDecoration='underline'
        width='fit-content'
      >
        {resources?.length} {CONTEXT_IDS_TEXT.RESOURCES_TEXT}
      </Text>
    </PopoverTrigger>
    <PopoverContent minW='lg'>
      <PopoverArrow />
      <PopoverBody>
        <Flex direction='column' gap={4} justifyContent='center' pb='4'>
          <Flex justifyContent='space-between' m={2}>
            <Text fontSize={13} color='neutral-fg' fontWeight='bold'>
              {CONTEXT_IDS_TEXT.RESOURCES_HEADER}
            </Text>
            <Text fontSize={13} color='neutral-fg' fontWeight='bold'>
              {CONTEXT_IDS_TEXT.TENANTS_HEADER}
            </Text>
          </Flex>
          {resources?.map(
            (
              {
                tenantDisplayName,
                tenantId,
                tenantProductCode,
                type,
                name,
                id,
              },
              i,
            ) => {
              const { iconUrl, name: displayName } =
                getResourceDisplayConfig({ tenantProductCode, id, type }) ?? {};
              const isEdge = tenantProductCode === 'Edge';
              const isSensitive = isSensitiveResource(
                name,
                tenantProductCode,
                type,
              );

              return (
                <Flex
                  key={`${tenantId}- ${i}`}
                  gap={5}
                  justifyContent='space-between'
                >
                  <Flex gap={2} alignItems='center'>
                    <ImageWithSkeleton
                      key={displayName}
                      width='7'
                      height='7'
                      src={iconUrl}
                      alt={displayName}
                      flexShrink={0}
                    />
                    <Flex alignItems='center' gap={2}>
                      <Text>{name}</Text>
                      <Badge
                        hidden={type !== 'edge'}
                        textTransform='capitalize'
                        colorScheme={isEdge ? 'success' : 'neutral'}
                      >
                        {isEdge
                          ? CONTEXT_IDS_TEXT.LIVE_STATUS
                          : CONTEXT_IDS_TEXT.PREVIEW_STATUS}
                      </Badge>
                      {isSensitive && (
                        <SensitiveResourceIcon size='sm' iconSize='16px' />
                      )}
                    </Flex>
                  </Flex>
                  <Text alignSelf='center' textAlign='end'>
                    {tenantDisplayName}
                  </Text>
                </Flex>
              );
            },
          )}
        </Flex>
      </PopoverBody>
    </PopoverContent>
  </Popover>
);

export const ResourcesCellData = ({
  resources,
}: Partial<FilteredContextWithResources>) => (
  <Flex direction='column' gap={2} justifyContent='center'>
    {resources?.map(
      (
        { tenantDisplayName, tenantId, tenantProductCode, type, name, id },
        i,
      ) => {
        const { iconUrl, name: displayName } =
          getResourceDisplayConfig({ tenantProductCode, id, type }) ?? {};
        const isSensitive = isSensitiveResource(name, tenantProductCode, type);

        return (
          <Flex key={`${tenantId}- ${i}`} gap={2} alignItems='center'>
            <ImageWithSkeleton
              key={displayName}
              width='7'
              height='7'
              src={iconUrl}
              alt={displayName}
              flexShrink={0}
            />
            <Flex alignItems='center' gap={2}>
              <Text>{name}</Text>
              <Badge
                hidden={type !== 'edge'}
                textTransform='capitalize'
                colorScheme={
                  tenantProductCode === TenantProductCode.EDGE
                    ? 'success'
                    : 'neutral'
                }
              >
                {tenantProductCode === TenantProductCode.EDGE
                  ? CONTEXT_IDS_TEXT.LIVE_STATUS
                  : CONTEXT_IDS_TEXT.PREVIEW_STATUS}
              </Badge>
              {isSensitive && (
                <SensitiveResourceIcon size='sm' iconSize='16px' />
              )}
            </Flex>
            <Text maxW='sm' isTruncated>
              {tenantDisplayName}
            </Text>
          </Flex>
        );
      },
    )}
  </Flex>
);

export const getTableOptions = () => ({
  rowActions: ({ original }: { original: FilteredContextWithResources }) => {
    const { masterSitecoreContextId, sitecoreContextId } = original;
    return [
      {
        id: '0',
        label: CONTEXT_IDS_TEXT.EDIT_ACTION,
        onClick: () => {
          history.push({
            pathname: getEditScopedContextRoute(
              masterSitecoreContextId,
              sitecoreContextId,
            ),
            search: history.location.search,
          });
        },
        'data-behavior-analytics-id': 'edit scoped context id',
      },
      {
        id: '1',
        label: CONTEXT_IDS_TEXT.DELETE_ACTION,
        onClick: () => {
          history.push({
            pathname: getDeleteScopedContextRoute(
              masterSitecoreContextId,
              sitecoreContextId,
            ),
            search: history.location.search,
          });
        },
        'data-behavior-analytics-id': 'delete scoped context id',
      },
    ];
  },
  columns: [
    {
      id: 'id',
      Header: CONTEXT_IDS_TEXT.SCOPED_CONTEXT_ID_COLUMN,
      accessor: (row: FilteredContextWithResources) => row.sitecoreContextId,
      Cell: ({ value }: { value: string }) => (
        <Text maxW='sm' isTruncated>
          {value}
        </Text>
      ),
      width: '25%',
      disableSortBy: false,
    },
    {
      id: 'name',
      Header: CONTEXT_IDS_TEXT.SCOPED_CONTEXT_NAME_COLUMN,
      accessor: (row: FilteredContextWithResources) => row.name,
      Cell: ({ value }: { value: string }) => (
        <Text maxW='sm' isTruncated>
          {value}
        </Text>
      ),
      width: '25%',
      disableSortBy: false,
    },
    {
      id: 'resources',
      Header: CONTEXT_IDS_TEXT.RESOURCES_APPLIED_COLUMN,
      accessor: (row: FilteredContextWithResources) => {
        // Sort by the first resource's name alphabetically
        const filteredResources = row.resources?.filter((r) =>
          isDisplayableResource(r),
        );
        return filteredResources?.[0]?.name?.toLowerCase() || '';
      },
      Cell: ({ row }: { row: { original: FilteredContextWithResources } }) => {
        const { resources } = row.original;
        return resources.length >= 3 ? (
          <ResourcesCellPopover resources={resources} />
        ) : (
          <ResourcesCellData resources={resources} />
        );
      },
      disableSortBy: false,
    },
  ],
});
